import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import {
  isClickedOutside,
  isMobileBreakpoint,
  isTouchDevice,
  mediaQueryLarge,
} from '@theme/utilities';

/**
 * A custom element that manages a product hotspot dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} trigger
 * @property {HTMLAnchorElement} productLink
 *
 * @extends Component<Refs>
 */
export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];

  /** Currently opened desktop hotspot */
  static activeDesktopHotspot = null;

  /** @type {(() => void) | null} */
  #pointerenterHandler = null;

  timer = /** @type {number | null} */ (null);

  connectedCallback() {
    super.connectedCallback();

    this.#handleBreakpointChange();

    mediaQueryLarge.addEventListener(
      'change',
      this.#handleBreakpointChange
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#removeDesktopListeners();

    mediaQueryLarge.removeEventListener(
      'change',
      this.#handleBreakpointChange
    );

    if (ProductHotspotComponent.activeDesktopHotspot === this) {
      ProductHotspotComponent.activeDesktopHotspot = null;
    }
  }

  /**
   * Open the quick-add modal on mobile/touch devices.
   * @returns {void}
   */
  #openQuickAddModal() {
    const quickAddComponent =
      /** @type {QuickAddComponent | null} */ (
        this.querySelector('quick-add-component')
      );

    if (!quickAddComponent) return;

    quickAddComponent.handleClick(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
      })
    );
  }

  /**
   * Desktop hover listener.
   * The dialog opens on hover and stays open.
   * @returns {void}
   */
  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;

    const pointerenterHandler = () => {
      if (dialog.open) return;

      if (this.timer) {
        clearTimeout(this.timer);
      }

      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.showDialog();
      }, 120);
    };

    this.#pointerenterHandler = pointerenterHandler;

    trigger.addEventListener(
      'pointerenter',
      pointerenterHandler
    );
  }

  /**
   * Remove desktop listeners.
   * @returns {void}
   */
  #removeDesktopListeners() {
    const { trigger } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener(
        'pointerenter',
        this.#pointerenterHandler
      );

      this.#pointerenterHandler = null;
    }

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * Handle responsive breakpoint changes.
   * @returns {void}
   */
  #handleBreakpointChange = () => {
    this.#removeDesktopListeners();

    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    } else {
      ProductHotspotComponent.activeDesktopHotspot = null;
    }
  };

  /**
   * Calculate dialog placement inside hotspot container.
   * @returns {Promise<void> | undefined}
   */
  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;

    const hotspotsContainer = this.parentElement;

    if (!hotspotsContainer) return;

    const BUTTON_GAP = 10;
    const CONTAINER_GAP = 10;
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    const containerRect =
      hotspotsContainer.getBoundingClientRect();

    const triggerRect = trigger.getBoundingClientRect();

    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');

    const {
      width: dialogWidth,
      height: dialogHeight,
    } = dialog.getBoundingClientRect();

    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');

    const buttonLeft =
      triggerRect.left - containerRect.left;

    const buttonRight =
      triggerRect.right - containerRect.left;

    const buttonTop =
      triggerRect.top - containerRect.top;

    const buttonBottom =
      triggerRect.bottom - containerRect.top;

    const spaceRight =
      containerRect.width -
      buttonRight -
      CONTAINER_GAP;

    const spaceLeft =
      buttonLeft -
      CONTAINER_GAP;

    let x = 'right';

    if (spaceRight >= dialogWidth + BUTTON_GAP) {
      x = 'right';
    } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
      x = 'left';
    } else {
      x = 'center';
    }

    let y = 'bottom';
    let verticalOffset = 0;

    if (x !== 'center') {
      let dialogStartY = buttonTop;
      let dialogEndY = buttonTop + dialogHeight;

      if (
        dialogEndY >
        containerRect.height - CONTAINER_GAP
      ) {
        dialogStartY =
          buttonBottom - dialogHeight;

        dialogEndY = buttonBottom;
        y = 'top';

        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset =
            CONTAINER_GAP - dialogStartY;
        } else if (
          dialogEndY >
          containerRect.height - CONTAINER_GAP
        ) {
          verticalOffset = -(
            dialogEndY -
            (containerRect.height - CONTAINER_GAP)
          );
        }
      } else if (dialogStartY < CONTAINER_GAP) {
        verticalOffset =
          CONTAINER_GAP - dialogStartY;

        y = 'bottom';
      }
    } else {
      if (
        containerRect.height - buttonBottom >=
        dialogHeight + TOTAL_GAP
      ) {
        y = 'bottom';
      } else if (
        buttonTop >=
        dialogHeight + TOTAL_GAP
      ) {
        y = 'top';
      } else {
        y =
          buttonTop < containerRect.height / 2
            ? 'bottom'
            : 'top';
      }
    }

    dialog.dataset.placement = `${x},${y}`;

    if (verticalOffset !== 0) {
      dialog.style.setProperty(
        '--dialog-vertical-offset',
        `${verticalOffset}px`
      );
    } else {
      dialog.style.removeProperty(
        '--dialog-vertical-offset'
      );
    }

    return new Promise((resolve) => {
      setTimeout(resolve, 100);
    });
  }

  /**
   * Product link getter.
   * @returns {HTMLAnchorElement | null}
   */
  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  /**
   * Hotspot click handler.
   * Mobile opens quick-add.
   * Desktop pins the product dialog open.
   *
   * @param {MouseEvent} event
   * @returns {void}
   */
  handleHotspotClick = (event) => {
    if (
      isMobileBreakpoint() ||
      isTouchDevice()
    ) {
      event.preventDefault();
      event.stopPropagation();

      this.#openQuickAddModal();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.showDialog();
  };

  /**
   * Open product dialog.
   * On desktop, close the previously opened hotspot first.
   *
   * @returns {Promise<void>}
   */
  showDialog = async () => {
    const { dialog } = this.refs;

    const isDesktop =
      !isMobileBreakpoint() &&
      !isTouchDevice();

    if (isDesktop) {
      const activeHotspot =
        ProductHotspotComponent.activeDesktopHotspot;

      if (
        activeHotspot &&
        activeHotspot !== this
      ) {
        await activeHotspot.closeDialog();
      }

      ProductHotspotComponent.activeDesktopHotspot =
        this;
    }

    if (dialog.open) return;

    await this.#calculateDialogPlacement();

    dialog.dataset.showing = 'true';
    delete dialog.dataset.closing;

    dialog.show();

    /*
     * Desktop:
     * Do not close when mouse leaves the hotspot/card.
     * Do not close when clicking outside.
     * It closes only when another hotspot opens or Escape is pressed.
     */
    if (isDesktop) {
      document.body.addEventListener(
        'keydown',
        this.lightDismissKeyboard
      );

      return;
    }

    /*
     * Mobile/touch:
     * Keep the existing dismissal behavior.
     */
    document.body.addEventListener(
      'click',
      this.lightDismissMouse
    );

    document.body.addEventListener(
      'keydown',
      this.lightDismissKeyboard
    );

    document.body.addEventListener(
      'keyup',
      this.lightDismissKeyboard
    );
  };

  /**
   * Close product dialog.
   *
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) {
      if (
        ProductHotspotComponent.activeDesktopHotspot ===
        this
      ) {
        ProductHotspotComponent.activeDesktopHotspot =
          null;
      }

      return;
    }

    dialog.dataset.closing = 'true';

    dialog.close();

    document.body.removeEventListener(
      'click',
      this.lightDismissMouse
    );

    document.body.removeEventListener(
      'keydown',
      this.lightDismissKeyboard
    );

    document.body.removeEventListener(
      'keyup',
      this.lightDismissKeyboard
    );

    const animations = dialog.getAnimations({
      subtree: true,
    });

    await Promise.allSettled(
      animations.map((animation) => animation.finished)
    );

    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;

      dialog.style.removeProperty(
        '--dialog-vertical-offset'
      );
    }

    if (
      ProductHotspotComponent.activeDesktopHotspot ===
      this
    ) {
      ProductHotspotComponent.activeDesktopHotspot =
        null;
    }
  };

  /**
   * Mobile/touch outside-click dismiss.
   *
   * @param {MouseEvent} event
   * @returns {void}
   */
  lightDismissMouse = (event) => {
    if (
      !isMobileBreakpoint() &&
      !isTouchDevice()
    ) {
      return;
    }

    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Escape key closes the dialog.
   * Mobile also keeps the original focus-loss behavior.
   *
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;

    if (
      event.type === 'keydown' &&
      event.key === 'Escape'
    ) {
      this.closeDialog();
      return;
    }

    if (
      (isMobileBreakpoint() || isTouchDevice()) &&
      event.type === 'keyup' &&
      !dialog.matches(
        ':is(:focus, :focus-visible, :focus-within)'
      )
    ) {
      this.closeDialog();
    }
  };
}

customElements.define(
  'product-hotspot-component',
  ProductHotspotComponent
);